'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { LayoutDashboard, LogIn, Menu, X } from 'lucide-react';
import { AppBanner } from '@/components/app-banner';
import { FeatureTicker } from '@/components/feature-ticker';

interface Props {
  hideLogin?: boolean;
  /** When set, replaces "Log in" with a dashboard link to the user's portal. */
  portalHref?: string;
  portalLabel?: string;
}

const DRAWER_DURATION_MS = 280;

export function PublicHeader({ hideLogin = false, portalHref, portalLabel }: Props) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const showPortal = Boolean(portalHref);

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMounted(true);
      return;
    }
    setVisible(false);
    const id = setTimeout(() => setMounted(false), DRAWER_DURATION_MS);
    return () => clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!mounted) return;
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf2);
    });
    return () => cancelAnimationFrame(raf1);
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [mounted]);

  return (
    <>
    {/* Visitors who are not signed in only (Safras, 24 Oct 2026): a portal
        link means someone is signed in. Pinned at the very top; the app
        banner and the header sit below it through --ticker-h. */}
    {showPortal ? null : <FeatureTicker />}
    <AppBanner />
    <header className="sticky top-[calc(var(--app-banner-h,0px)+var(--ticker-h,0px))] z-30 bg-[var(--sidebar-bg)]">
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 md:px-6">
        <Link href="/" aria-label="Frags For Less" className="inline-flex items-center">
          <Image
            src="/frags-for-less-logo.png"
            alt="Frags For Less"
            width={1603}
            height={420}
            priority
            className="h-8 w-auto"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-3 sm:flex">
          <Link
            href="/"
            className="font-display rounded-full px-3 py-2 text-sm font-normal text-white/75 transition-colors duration-200 hover:bg-white/10 hover:text-white"
          >
            Home
          </Link>
          {/* The one nav item that has to stand out (Safras, 22 Sep 2026). A
              tinted chip with an orange edge, NOT a second solid pill: Log in
              is the only filled orange in this bar and it stays that way. Its
              text is full white too, where the other links carry white/75. */}
          <Link
            href="/catalog"
            className="font-display rounded-full border border-[var(--accent)]/50 bg-[var(--accent-soft)] px-3 py-2 text-sm font-medium text-white transition-colors duration-200 hover:border-[var(--accent)] hover:bg-[var(--accent)]/30"
          >
            Catalog
          </Link>
          <Link
            href="/how-it-works"
            className="font-display rounded-full px-3 py-2 text-sm font-normal text-white/75 transition-colors duration-200 hover:bg-white/10 hover:text-white"
          >
            How it works
          </Link>
          <Link
            href="/about"
            className="font-display rounded-full px-3 py-2 text-sm font-normal text-white/75 transition-colors duration-200 hover:bg-white/10 hover:text-white"
          >
            About
          </Link>
          <Link
            href="/contact"
            className="font-display rounded-full px-3 py-2 text-sm font-normal text-white/75 transition-colors duration-200 hover:bg-white/10 hover:text-white"
          >
            Contact
          </Link>
          {showPortal ? (
            <Link
              href={portalHref!}
              style={{
                backgroundImage:
                  'linear-gradient(135deg, var(--accent) 0%, var(--accent-active) 100%)',
              }}
              className="group font-display inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:brightness-105"
            >
              <LayoutDashboard className="size-3.5" />
              {portalLabel ?? 'Go to portal'}
            </Link>
          ) : hideLogin ? null : (
            <Link
              href="/login"
              style={{
                backgroundImage:
                  'linear-gradient(135deg, var(--accent) 0%, var(--accent-active) 100%)',
              }}
              className="group font-display inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:brightness-105"
            >
              <LogIn className="size-3.5" />
              Log in
            </Link>
          )}
        </nav>

        {/* Mobile: login pill (or portal pill) + hamburger trigger */}
        <div className="flex items-center gap-2 sm:hidden">
          {showPortal ? (
            <Link
              href={portalHref!}
              style={{
                backgroundImage:
                  'linear-gradient(135deg, var(--accent) 0%, var(--accent-active) 100%)',
              }}
              className="font-display inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium text-white transition-all hover:brightness-105"
            >
              <LayoutDashboard className="size-3.5" />
              Portal
            </Link>
          ) : hideLogin ? null : (
            <Link
              href="/login"
              style={{
                backgroundImage:
                  'linear-gradient(135deg, var(--accent) 0%, var(--accent-active) 100%)',
              }}
              className="font-display inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium text-white transition-all hover:brightness-105"
            >
              <LogIn className="size-3.5" />
              Log in
            </Link>
          )}
          <button
            type="button"
            aria-label="Open menu"
            aria-expanded={open}
            onClick={() => setOpen(true)}
            className="inline-flex size-11 items-center justify-center rounded-full text-white/85 transition-colors hover:bg-white/10 hover:text-white"
          >
            <Menu className="size-6" />
          </button>
        </div>
      </div>
    </header>

    {/* Mobile drawer - rendered as a sibling of <header> so it isn't trapped
        inside the header's sticky stacking context. */}
      {mounted ? (
        <div className="fixed inset-0 z-50 sm:hidden" role="dialog" aria-modal="true">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-[280ms] ease-out ${
              visible ? 'opacity-100' : 'opacity-0'
            }`}
          />
          <div
            style={{ backgroundColor: 'var(--sidebar-bg)' }}
            className={`fixed inset-y-0 right-0 flex w-[85%] max-w-sm flex-col p-6 ${
              visible ? '' : 'hidden'
            }`}
          >
            <div className="mb-8 flex items-center justify-between">
              <Link
                href="/"
                aria-label="Frags For Less home"
                onClick={() => setOpen(false)}
                className="inline-flex items-center"
              >
                <Image
                  src="/frags-for-less-logo.png"
                  alt="Frags For Less"
                  width={1603}
                  height={420}
                  className="h-7 w-auto"
                />
              </Link>
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="inline-flex size-11 items-center justify-center rounded-full text-white/85 hover:bg-white/10 hover:text-white"
              >
                <X className="size-6" />
              </button>
            </div>

            <nav className="flex flex-col gap-1 text-base">
              <MobileLink href="/" onClick={() => setOpen(false)}>
                Home
              </MobileLink>
              <MobileLink href="/catalog" highlight onClick={() => setOpen(false)}>
                Catalog
              </MobileLink>
              <MobileLink href="/how-it-works" onClick={() => setOpen(false)}>
                How it works
              </MobileLink>
              <MobileLink href="/about" onClick={() => setOpen(false)}>
                About
              </MobileLink>
              <MobileLink href="/contact" onClick={() => setOpen(false)}>
                Contact
              </MobileLink>
              {showPortal ? (
                <MobileLink href={portalHref!} onClick={() => setOpen(false)}>
                  {portalLabel ?? 'Go to portal'}
                </MobileLink>
              ) : null}
            </nav>

            <div className="mt-auto space-y-3">
              {showPortal ? (
                <Link
                  href={portalHref!}
                  onClick={() => setOpen(false)}
                  style={{
                    backgroundImage:
                      'linear-gradient(135deg, var(--accent) 0%, var(--accent-active) 100%)',
                  }}
                  className="font-display flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white transition-all hover:brightness-105"
                >
                  <LayoutDashboard className="size-4" />
                  {portalLabel ?? 'Go to portal'}
                </Link>
              ) : (
                <>
                  <Link
                    href="/signup"
                    onClick={() => setOpen(false)}
                    style={{
                      backgroundImage:
                        'linear-gradient(135deg, var(--accent) 0%, var(--accent-active) 100%)',
                    }}
                    className="font-display flex w-full items-center justify-center rounded-full px-5 py-3 text-sm font-semibold text-white transition-all hover:brightness-105"
                  >
                    Create wholesale account
                  </Link>
                  {hideLogin ? null : (
                    <Link
                      href="/login"
                      onClick={() => setOpen(false)}
                      className="font-display flex w-full items-center justify-center rounded-full border border-white/20 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
                    >
                      Log in
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function MobileLink({
  href,
  onClick,
  highlight = false,
  children,
}: {
  href: string;
  onClick: () => void;
  /** Marks the one item that should read as the way in, not just another row.
   *  Same tinted chip as the desktop bar so the two menus agree. */
  highlight?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className={
        highlight
          ? 'font-display rounded-xl border border-[var(--accent)]/50 bg-[var(--accent-soft)] px-3 py-3 font-semibold text-white transition-colors hover:border-[var(--accent)] hover:bg-[var(--accent)]/30'
          : 'font-display rounded-xl px-3 py-3 font-medium text-white/85 transition-colors hover:bg-white/10 hover:text-white'
      }
    >
      {children}
    </Link>
  );
}
